import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useCallback, useMemo, useState } from 'react'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { useTemplateList, LIST_TEMPLATES } from '../../graphql/templateQueries'
import { DELETE_TEMPLATE } from '../../graphql/templateMutations'
import TemplatePreview from './TemplatePreview'
import { formatDateTime } from 'utilities/time'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const TemplatesTab = () => {
  const navigate = useNavigate()
  const { loading, error, data } = useTemplateList()
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const refetchConfig = {
    refetchQueries: [{ query: LIST_TEMPLATES, variables: { limit: 100 } }],
  }

  const [deleteTemplate] = useMutation(DELETE_TEMPLATE, refetchConfig)

  const templates = useMemo(
    () => data?.contentTemplateList?.templates || [],
    [data],
  )

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates

    const query = searchQuery.toLowerCase()
    return templates.filter((tmpl) => {
      return (
        tmpl.name.toLowerCase().includes(query) ||
        tmpl.id.toLowerCase().includes(query) ||
        tmpl.channel.toLowerCase().includes(query) ||
        (tmpl.description || '').toLowerCase().includes(query)
      )
    })
  }, [templates, searchQuery])

  const handleDelete = useCallback(
    async (templateId) => {
      await deleteTemplate({ variables: { input: { id: templateId } } })
      setDeleteConfirmId(null)
    },
    [deleteTemplate],
  )

  if (loading && !data) return <Loading />
  if (error)
    return (
      <Alert severity="error">Failed to load templates: {error.message}</Alert>
    )

  return (
    <Box>
      <AdminPageHeader>
        <Typography variant="h6">
          Templates ({filteredTemplates.length})
        </Typography>
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(ROUTE_METADATA.adminOutreachTemplateNew.path)}
          startIcon={<AddIcon />}
        >
          New Template
        </Button>
      </AdminPageHeader>

      <Paper elevation={1} sx={{ p: 2 }}>
        <TextField
          placeholder="Search templates by name, ID, channel, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((tmpl) => (
                <TableRow
                  key={tmpl.id}
                  hover
                  onClick={() =>
                    navigate(
                      `${ROUTE_METADATA.adminOutreachTemplates.path}/${tmpl.id}`,
                    )
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tmpl.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tmpl.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tmpl.channel}
                      size="small"
                      color={tmpl.channel === 'email' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {tmpl.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {tmpl.updatedAt
                      ? formatDateTime(tmpl.updatedAt, 'MMM DD, YYYY')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPreviewTemplate(tmpl)
                      }}
                      title="Preview"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(
                          `${ROUTE_METADATA.adminOutreachTemplates.path}/${tmpl.id}`,
                        )
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirmId(tmpl.id)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <TemplatePreview
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure? This will fail if the template is in use by any
            campaigns.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => handleDelete(deleteConfirmId)}
            sx={{ backgroundColor: 'error.main' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TemplatesTab
